<template>
    <div class="statistics-chart-visits" ref="statistics-chart-visits">
        <v-chart v-bind="{ options }" class="statistics-chart-visits__chart" :key="key" />
    </div>
</template>

<script>
import "echarts";
import dayjs from "@/plugins/vue-dayjs";
import VChart from "vue-echarts";

const reducer_method = ({ min, max, interval }, value, index, array) => {
    if (value < min) {
        min = value;
    }

    if (max < value) {
        max = value;
    }

    const spread = max - min;
    const unit = spread < 5 ? 1 : spread < 10 ? 2 : spread < 25 ? 5 : spread < 50 ? 10 : spread < 100 ? 20 : spread < 250 ? 50 : spread < 500 ? 100 : 200;

    if (array.length - 1 == index) {
        min = (Math.ceil(min / unit) - 1) * unit;
        if (min < 0) min = 0;

        max = (Math.floor(max / unit) + 1) * unit;

        interval = max / 5;
        if (interval < unit) interval = unit;
        else if (interval % unit) interval = Math.floor(interval / unit) * unit;
    }

    return { min, max, interval };
};
const reducer_object = { min: 0, max: 0, interval: 0 };

export default {
    components: {
        VChart,
    },
    props: {
        statistics: { type: Array, default: () => [] },
        searchDateValue: { type: Array, default: () => [] },
    },
    data: () => ({
        key: 0,

        width: undefined,
    }),
    computed: {
        xAxis_data() {
            let [startAt, endAt] = this.searchDateValue;

            if (!startAt) startAt = [...this.statistics.map(({ _id }) => _id).sort()].shift() ?? Date.now();
            if (!endAt) endAt = [...this.statistics.map(({ _id }) => _id).sort()].pop() ?? Date.now();

            let data = [startAt.toDate()];
            do {
                data.push(dayjs(data.at(-1)).add(1, "day").toISOString().toDate());
            } while (dayjs(data.at(-1)).isBefore(endAt, "date"));

            return data;
        },
        xAxis_label_interval() {
            let count = this.xAxis_data.length;

            if (count < 5) return undefined;
            if (count < 10) return 2;
            if (count < 15) return 3;
            if (count < 32) return 7;
            if (count < 184) return 30;
            if (count < 367) return 60;

            return 365;
        },

        yAxis_data() {
            return this.xAxis_data.map((date) => ({
                date,
                users: 0,
                pages: 0,
                ...this.statistics.find(({ _id }) => _id == date),
            }));
        },
        yAxis_data_pages() {
            return this.yAxis_data.map(({ pages }) => pages);
        },
        yAxis_data_users() {
            return this.yAxis_data.map(({ users }) => users);
        },
        yAxis_conf_pages() {
            return this.yAxis_data_pages.reduce(reducer_method, reducer_object);
        },
        yAxis_conf_users() {
            return this.yAxis_data_users.reduce(reducer_method, reducer_object);
        },
        options() {
            return {
                legend: {
                    top: 8,
                    data: ["페이지뷰", "방문자수"],
                },
                tooltip: {
                    trigger: "axis",
                },
                grid: [
                    {
                        top: 40,
                        left: "center",
                        height: 312 - 40 - 1 - 28,
                        width: this.width - 88,
                    },
                ],
                xAxis: [
                    {
                        type: "category",
                        data: this.xAxis_data,
                        axisLabel: {
                            interval: this.xAxis_label_interval,
                            formatter(value) {
                                return dayjs(value).format("M월 D일");
                            },
                        },
                    },
                ],
                yAxis: [
                    {
                        type: "value",
                        name: "페이지뷰",
                        ...this.yAxis_conf_pages,
                    },
                    {
                        type: "value",
                        name: "방문자수",
                        ...this.yAxis_conf_users,
                        splitLine: {
                            lineStyle: {
                                type: "dashed",
                            },
                        },
                    },
                ],
                series: [
                    {
                        name: "페이지뷰",
                        type: "line",
                        yAxisIndex: 0,
                        animation: false,
                        data: this.yAxis_data_pages,
                        smooth: true,
                    },
                    {
                        name: "방문자수",
                        type: "line",
                        yAxisIndex: 1,
                        animation: false,
                        data: this.yAxis_data_users,
                        smooth: true,
                    },
                ],
            };
        },
    },
    methods: {
        init() {
            window.addEventListener("resize", this.calc);
        },
        calc() {
            const intervalId = setInterval(() => {
                const $el = this.$refs["statistics-chart-visits"];
                if (!$el) return;
                this.width = $el.getBoundingClientRect()?.width;
                clearInterval(intervalId);
            }, 10);
        },
        unddo() {
            window.removeEventListener("resize", this.calc);
        },
    },
    watch: {
        statistics() {
            this.key += 1;
        },
        width() {
            this.key += 1;
        },
    },
    mounted() {
        this.init();
        this.calc();
    },
};
</script>

<style lang="scss" scoped>
.statistics-chart-visits {
    &__chart {
        height: 312px;
        width: 100%;
        overflow: hidden;
    }
}
</style>